import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import ImageUpload from "../components/site/ImageUpload";
import { Trash2, Pencil, Plus, Save } from "lucide-react";

const TABS = ["stats", "lakes", "items", "catches", "bookings", "events", "rules", "users"];

const emptyLake = { name_en: "", name_hu: "", description_en: "", description_hu: "", area_hectares: 0, max_depth_m: 0, fish_species_en: [], fish_species_hu: [], image_base64: null, order: 0 };
const emptyItem = { name_en: "", name_hu: "", description_en: "", description_hu: "", price_per_night: 0, capacity: 2, image_base64: null, amenities_en: [], amenities_hu: [], type: "spot" };
const emptyEvent = { title_en: "", title_hu: "", description_en: "", description_hu: "", start_date: "", end_date: "", image_base64: null, location_en: "", location_hu: "" };

export default function Admin() {
  const { t } = useLang();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [lakes, setLakes] = useState([]);
  const [items, setItems] = useState([]);
  const [catches, setCatches] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [rules, setRules] = useState({ rules_en: "", rules_hu: "", advices_en: "", advices_hu: "" });

  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [s, l, it, c, b, e, u, r] = await Promise.all([
        api.get("/admin/stats"), api.get("/lakes"), api.get("/items"),
        api.get("/catches", { params: { limit: 200 } }), api.get("/bookings"),
        api.get("/events"), api.get("/admin/users"), api.get("/rules"),
      ]);
      setStats(s.data); setLakes(l.data); setItems(it.data); setCatches(c.data);
      setBookings(b.data); setEvents(e.data); setUsers(u.data); setRules(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const handleSaveLake = async (lake) => {
    try {
      const payload = {
        ...lake,
        area_hectares: parseFloat(lake.area_hectares) || 0,
        max_depth_m: parseFloat(lake.max_depth_m) || 0,
        order: parseInt(lake.order) || 0,
      };
      if (lake.id) await api.put(`/lakes/${lake.id}`, payload);
      else await api.post("/lakes", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const handleSaveItem = async (it) => {
    try {
      const payload = {
        ...it,
        price_per_night: parseFloat(it.price_per_night) || 0,
        capacity: parseInt(it.capacity) || 1,
      };
      if (it.id) await api.put(`/items/${it.id}`, payload);
      else await api.post("/items", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const handleSaveEvent = async (ev) => {
    try {
      if (ev.id) await api.put(`/events/${ev.id}`, ev);
      else await api.post("/events", ev);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const del = async (path) => {
    if (!confirm("Delete this item?")) return;
    try { await api.delete(path); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const saveRules = async () => {
    try { await api.put("/rules", rules); toast.success("Rules saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="container-deg py-16 md:py-20" data-testid="admin-page">
      <div className="overline">07 · {t("nav.admin")}</div>
      <h1 className="font-serif text-4xl md:text-5xl mt-3">{t("admin.heading")}</h1>
      <p className="text-ink-soft mt-3">{t("admin.sub")}</p>

      <div className="flex gap-1 border-b border-line mt-10 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((k) => (
          <button
            key={k}
            onClick={() => { setTab(k); setEditing(null); }}
            className={`px-4 py-3 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${tab === k ? "border-accent text-primary font-semibold" : "border-transparent text-ink-soft hover:text-primary"}`}
            data-testid={`admin-tab-${k}`}
          >
            {t(`admin.tabs.${k}`)}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "stats" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(stats).map(([k, v]) => (
              <div key={k} className="card-deg p-6" data-testid={`stat-${k}`}>
                <div className="overline">{k}</div>
                <div className="font-serif text-4xl mt-2 text-primary">{typeof v === "number" ? v : String(v)}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "lakes" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={() => setEditing({ kind: "lake", data: { ...emptyLake } })} className="btn-primary" data-testid="add-lake">
                <Plus size={16} className="mr-2" /> {t("admin.add")}
              </button>
            </div>
            {lakes.map((l) => (
              <div key={l.id} className="card-deg p-6 flex items-center justify-between gap-4" data-testid={`admin-lake-${l.id}`}>
                <div>
                  <div className="font-serif text-2xl">{l.name_en}</div>
                  <div className="text-xs text-ink-soft mt-1">{l.area_hectares} ha · {l.max_depth_m} m</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ kind: "lake", data: { ...l } })} className="p-2 border border-line hover:border-primary"><Pencil size={14} /></button>
                  <button onClick={() => del(`/lakes/${l.id}`)} className="p-2 border border-line hover:border-destructive hover:text-destructive"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "items" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={() => setEditing({ kind: "item", data: { ...emptyItem } })} className="btn-primary" data-testid="add-item">
                <Plus size={16} className="mr-2" /> {t("admin.add")}
              </button>
            </div>
            {items.map((i) => (
              <div key={i.id} className="card-deg p-6 flex items-center justify-between gap-4">
                <div>
                  <div className="overline">{i.type}</div>
                  <div className="font-serif text-2xl mt-1">{i.name_en}</div>
                  <div className="text-xs text-ink-soft mt-1">€{i.price_per_night}/night · cap {i.capacity}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ kind: "item", data: { ...i } })} className="p-2 border border-line"><Pencil size={14} /></button>
                  <button onClick={() => del(`/items/${i.id}`)} className="p-2 border border-line hover:border-destructive hover:text-destructive"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "catches" && (
          <div className="space-y-3">
            {catches.map((c) => (
              <div key={c.id} className="card-deg p-4 flex items-center gap-4">
                <div className="w-16 h-16 bg-bg-muted overflow-hidden shrink-0">
                  {c.image_base64 && <img src={c.image_base64} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="font-serif text-lg">{c.species_en} · {c.weight_kg} kg</div>
                  <div className="text-xs text-ink-soft">{c.user_name} · {new Date(c.created_at).toLocaleDateString()}</div>
                </div>
                <button onClick={() => del(`/catches/${c.id}`)} className="p-2 border border-line hover:border-destructive hover:text-destructive"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "bookings" && (
          <div className="overflow-x-auto card-deg">
            <table className="min-w-full text-sm">
              <thead><tr className="border-b border-line text-left">
                <th className="px-4 py-3 overline">User</th>
                <th className="px-4 py-3 overline">Item</th>
                <th className="px-4 py-3 overline">Dates</th>
                <th className="px-4 py-3 overline">Total</th>
                <th className="px-4 py-3 overline">Status</th>
                <th className="px-4 py-3 overline">Payment</th>
              </tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-line/60">
                    <td className="px-4 py-3">{b.user_name}<div className="text-xs text-ink-soft">{b.user_email}</div></td>
                    <td className="px-4 py-3">{b.item_name_en}<div className="text-xs text-ink-soft">{b.item_type}</div></td>
                    <td className="px-4 py-3">{b.start_date} → {b.end_date}</td>
                    <td className="px-4 py-3">€{b.total_amount}</td>
                    <td className="px-4 py-3">{b.status}</td>
                    <td className="px-4 py-3">{b.payment_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "events" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={() => setEditing({ kind: "event", data: { ...emptyEvent } })} className="btn-primary"><Plus size={16} className="mr-2" /> {t("admin.add")}</button>
            </div>
            {events.map((e) => (
              <div key={e.id} className="card-deg p-6 flex items-center justify-between">
                <div>
                  <div className="font-serif text-2xl">{e.title_en}</div>
                  <div className="text-xs text-ink-soft mt-1">{new Date(e.start_date).toLocaleString()}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ kind: "event", data: { ...e } })} className="p-2 border border-line"><Pencil size={14} /></button>
                  <button onClick={() => del(`/events/${e.id}`)} className="p-2 border border-line hover:border-destructive"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "rules" && (
          <div className="space-y-6">
            {["rules_en", "rules_hu", "advices_en", "advices_hu"].map((k) => (
              <div key={k}>
                <label className="overline">{k.replace("_", " ").toUpperCase()}</label>
                <textarea rows={6} value={rules[k] || ""} onChange={(ev) => setRules({ ...rules, [k]: ev.target.value })} className="w-full mt-2 p-4 bg-bg-paper border border-line font-sans text-sm focus:outline-none focus:border-primary" data-testid={`admin-rules-${k}`} />
              </div>
            ))}
            <button onClick={saveRules} className="btn-primary" data-testid="admin-save-rules"><Save size={16} className="mr-2" /> {t("admin.save")}</button>
          </div>
        )}

        {tab === "users" && (
          <div className="overflow-x-auto card-deg">
            <table className="min-w-full text-sm">
              <thead><tr className="border-b border-line text-left">
                <th className="px-4 py-3 overline">Name</th>
                <th className="px-4 py-3 overline">Email</th>
                <th className="px-4 py-3 overline">Role</th>
                <th className="px-4 py-3 overline">Joined</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-line/60">
                    <td className="px-4 py-3">{u.name}</td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs ${u.role === "admin" ? "bg-accent text-white" : "bg-bg-muted"}`}>{u.role}</span></td>
                    <td className="px-4 py-3 text-ink-soft">{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[70] bg-primary/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-bg-paper max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-8 py-5 border-b border-line flex justify-between items-center">
              <h3 className="font-serif text-2xl capitalize">{editing.kind}</h3>
              <button onClick={() => setEditing(null)} className="text-ink-soft hover:text-primary">✕</button>
            </div>
            <div className="p-8 space-y-5">
              {editing.kind === "lake" && (
                <LakeForm data={editing.data} onChange={(d) => setEditing({ ...editing, data: d })} onSave={() => handleSaveLake(editing.data)} />
              )}
              {editing.kind === "item" && (
                <ItemForm data={editing.data} onChange={(d) => setEditing({ ...editing, data: d })} onSave={() => handleSaveItem(editing.data)} />
              )}
              {editing.kind === "event" && (
                <EventForm data={editing.data} onChange={(d) => setEditing({ ...editing, data: d })} onSave={() => handleSaveEvent(editing.data)} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function LakeForm({ data, onChange, onSave }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name EN"><input className="input-deg" value={data.name_en} onChange={(e) => set("name_en", e.target.value)} /></Field>
        <Field label="Name HU"><input className="input-deg" value={data.name_hu} onChange={(e) => set("name_hu", e.target.value)} /></Field>
      </div>
      <Field label="Description EN"><textarea rows={3} className="input-deg" value={data.description_en} onChange={(e) => set("description_en", e.target.value)} /></Field>
      <Field label="Description HU"><textarea rows={3} className="input-deg" value={data.description_hu} onChange={(e) => set("description_hu", e.target.value)} /></Field>
      <div className="grid grid-cols-3 gap-4">
        <Field label="Area (ha)"><input type="number" step="0.1" className="input-deg" value={data.area_hectares} onChange={(e) => set("area_hectares", e.target.value)} /></Field>
        <Field label="Max depth (m)"><input type="number" step="0.1" className="input-deg" value={data.max_depth_m} onChange={(e) => set("max_depth_m", e.target.value)} /></Field>
        <Field label="Order"><input type="number" className="input-deg" value={data.order} onChange={(e) => set("order", e.target.value)} /></Field>
      </div>
      <Field label="Species EN (comma)"><input className="input-deg" value={(data.fish_species_en || []).join(", ")} onChange={(e) => set("fish_species_en", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></Field>
      <Field label="Species HU (comma)"><input className="input-deg" value={(data.fish_species_hu || []).join(", ")} onChange={(e) => set("fish_species_hu", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></Field>
      <Field label="Photo"><ImageUpload value={data.image_base64} onChange={(v) => set("image_base64", v)} testId="lake-image" /></Field>
      <button className="btn-primary" onClick={onSave} data-testid="save-lake"><Save size={16} className="mr-2" /> Save</button>
    </>
  );
}

function ItemForm({ data, onChange, onSave }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <>
      <Field label="Type">
        <select className="input-deg" value={data.type} onChange={(e) => set("type", e.target.value)}>
          <option value="spot">Fishing swim / spot</option>
          <option value="house">Cabin / house</option>
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name EN"><input className="input-deg" value={data.name_en} onChange={(e) => set("name_en", e.target.value)} /></Field>
        <Field label="Name HU"><input className="input-deg" value={data.name_hu} onChange={(e) => set("name_hu", e.target.value)} /></Field>
      </div>
      <Field label="Description EN"><textarea rows={3} className="input-deg" value={data.description_en} onChange={(e) => set("description_en", e.target.value)} /></Field>
      <Field label="Description HU"><textarea rows={3} className="input-deg" value={data.description_hu} onChange={(e) => set("description_hu", e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Price / night (€)"><input type="number" step="0.5" className="input-deg" value={data.price_per_night} onChange={(e) => set("price_per_night", e.target.value)} /></Field>
        <Field label="Capacity"><input type="number" className="input-deg" value={data.capacity} onChange={(e) => set("capacity", e.target.value)} /></Field>
      </div>
      <Field label="Amenities EN (comma)"><input className="input-deg" value={(data.amenities_en || []).join(", ")} onChange={(e) => set("amenities_en", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></Field>
      <Field label="Amenities HU (comma)"><input className="input-deg" value={(data.amenities_hu || []).join(", ")} onChange={(e) => set("amenities_hu", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></Field>
      <Field label="Photo"><ImageUpload value={data.image_base64} onChange={(v) => set("image_base64", v)} testId="item-image" /></Field>
      <button className="btn-primary" onClick={onSave} data-testid="save-item"><Save size={16} className="mr-2" /> Save</button>
    </>
  );
}

function EventForm({ data, onChange, onSave }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Title EN"><input className="input-deg" value={data.title_en} onChange={(e) => set("title_en", e.target.value)} /></Field>
        <Field label="Title HU"><input className="input-deg" value={data.title_hu} onChange={(e) => set("title_hu", e.target.value)} /></Field>
      </div>
      <Field label="Description EN"><textarea rows={3} className="input-deg" value={data.description_en} onChange={(e) => set("description_en", e.target.value)} /></Field>
      <Field label="Description HU"><textarea rows={3} className="input-deg" value={data.description_hu} onChange={(e) => set("description_hu", e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Start (ISO)"><input className="input-deg" value={data.start_date} onChange={(e) => set("start_date", e.target.value)} placeholder="2026-06-13T10:00:00+00:00" /></Field>
        <Field label="End (ISO, optional)"><input className="input-deg" value={data.end_date || ""} onChange={(e) => set("end_date", e.target.value)} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Location EN"><input className="input-deg" value={data.location_en || ""} onChange={(e) => set("location_en", e.target.value)} /></Field>
        <Field label="Location HU"><input className="input-deg" value={data.location_hu || ""} onChange={(e) => set("location_hu", e.target.value)} /></Field>
      </div>
      <Field label="Photo"><ImageUpload value={data.image_base64} onChange={(v) => set("image_base64", v)} testId="event-image" /></Field>
      <button className="btn-primary" onClick={onSave} data-testid="save-event"><Save size={16} className="mr-2" /> Save</button>
    </>
  );
}
